import React, { useState, useEffect } from 'react';
import { usePhysicalDeliveryStore } from 'store/DeliveryStore';
import '../../styles/ProductCard.css';
import { toast } from 'react-toastify';
import { formatINR } from 'utils';
import BuyCoinsBarsService from '../../services/buy-coins-bars.service';

const ProductCard = ({ product, nextStep, getSingleProduct, isFirst }) => {
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [notifyRequested, setNotifyRequested] = useState(false);
  const [notifyLoading, setNotifyLoading] = useState(false);
  const setSelectedProduct = usePhysicalDeliveryStore(
    (state) => state.setSelectedProduct
  );

  const handleNotifyMeClick = async () => {
    setNotifyLoading(true);
    try {
      const response = await BuyCoinsBarsService.notifyOnStock({
        weight: product.weight,
        metalCode: product.metalCode,
      });
      if (response?.status === 200 || response?.status === 201) {
        setNotifyRequested(true);
        toast.success("We'll notify you once this product is back in stock.");
      } else {
        toast.error('Failed to set up notification. Please try again.');
      }
    } catch (error) {
      console.error('Error requesting stock notification:', error);
      toast.error('Failed to set up notification. Please try again.');
    } finally {
      setNotifyLoading(false);
    }
  };

  const handleBuyNowClick = async () => {
    try {
      const productDetails = await getSingleProduct(product.itemCode);

       if (!productDetails || !productDetails.itemCode) {
      toast.error("Product details not available right now. Please try again.");
      return;
    }
      setSelectedProduct(productDetails);
      nextStep();
    } catch (error) {
      console.error('Error fetching product details:', error);
      toast.error("Failed to fetch product details. Please try again later.");
    }
  };

  //  Auto-swiping effect for the first card
  useEffect(() => {
    if (!isFirst || !product.allImages) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) =>
        prev === product.allImages.length - 1 ? 0 : prev + 1
      );
    }, 2000); // change image every 2s

    return () => clearInterval(interval);
  }, [isFirst, product.allImages]);

  const displayedImage = isFirst
    ? product.allImages?.[currentIndex] || product.image
    : product.image;

  return (
    <div className='product-card'>
      {/* Image with Skeleton Effect */}
      <div className='product-image-wrapper'>
        {loading && <div className='skeleton skeleton-image'></div>}
        {!loading && !product.inStock && (
          <span className='out-of-stock-ribbon'>Out of Stock</span>
        )}
        <img
          src={displayedImage}
          alt={product.productName}
          className={`product-image ${loading ? 'hidden' : ''}`}
          onLoad={() => setLoading(false)}
        />
      </div>

      {/* Product Name Skeleton */}
      <p className={`product-name ${loading ? 'skeleton skeleton-text' : ''}`}>
        {!loading && `${product.productName} (Purity ${product.purity}%)`}
      </p>

      {/* Price Section */}
      <div className='price-section'>
        {Number(product.discountedAmount) > 0 ? (
          <>
            <span
              className={`original-price ${
                loading ? 'skeleton skeleton-price' : ''
              }`}
            >
              {!loading && formatINR(product.price)}
            </span>
            <span
              className={`discounted-price ${
                loading ? 'skeleton skeleton-price' : ''
              }`}
            >
              {!loading && formatINR(product.discountedPrice)}
            </span>
          </>
        ) : (
          <span
            className={`discounted-price ${
              loading ? 'skeleton skeleton-price' : ''
            }`}
          >
            {!loading && formatINR(product.price)}
          </span>
        )}
      </div>

      <hr className='product-divider' />

      {/* Button */}
      {!loading && !product.inStock ? (
        <button
          className={`buy-button notify-button ${
            notifyRequested ? 'disabled' : ''
          }`}
          disabled={notifyLoading || notifyRequested}
          onClick={handleNotifyMeClick}
        >
          {notifyRequested
            ? "We'll notify you"
            : notifyLoading
            ? 'Please wait...'
            : 'Notify Me'}
        </button>
      ) : (
        <button
          className={`buy-button ${loading ? 'skeleton skeleton-button' : ''}`}
          disabled={loading}
          onClick={handleBuyNowClick}
        >
          {!loading ? 'Buy Now' : ''}
        </button>
      )}
    </div>
  );
};

export default ProductCard;
